import { Box, CircularProgress, Drawer, Link, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Select, Skeleton, Stack, Typography, useColorScheme } from "@mui/material"
import { ApiQueries, DaymapIcon, DaymapIconType, Toast, ToastControls, useAuth, useSchool, openExternalUrl } from 'daymap-react';
import { MainMenuItem } from "../..";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

type MoreDrawerProps = {
    open: boolean;
    menus: MainMenuItem[];
    onClose: () => void;
}

export const MoreDrawer: React.FC<MoreDrawerProps> = ({open, menus, onClose}) => {
    const { baseUrl } = useSchool();
    const aboutlicensesUrl = `${baseUrl}/daymap/unsecured/aboutlicenses.aspx`;

    return <Drawer open={open} anchor="right" onClose={onClose}>
        <Stack spacing={2} p={2} sx={{ height: "100%", maxWidth: '80vw', paddingTop: `calc(env(safe-area-inset-top) + 25px)`, paddingBottom: `env(safe-area-inset-bottom)` }}>
            <LoggedInAs />
            <Menus menus={menus} onClose={onClose} />

            {/* Align links to bottom */}
            <Box sx={{ flexGrow: 1 }} />
            <Stack spacing={1}>
                <Typography variant="caption">
                    <Link
                        component="button"
                        color="info"
                        onClick={() => openExternalUrl("https://daymapguides.blob.core.windows.net/externalshared/Loop%20Software%20Daymap%20Privacy%20Policy.pdf?sp=r&st=2025-06-06T00:00:00Z&se=2030-01-01T00:00:00Z&spr=https&sv=2024-11-04&sr=b&sig=LR4OKcU0nNNB2NY2ZsTNhypqNhueTzci%2BRGJCVlr1Aw%3D")}
                    >
                        Privacy Policy
                    </Link>
                </Typography>
                <Typography variant="caption">
                    <Link
                        component="button"
                        color="info"
                        onClick={() => openExternalUrl("https://daymapguides.blob.core.windows.net/externalshared/Acceptable%20of%20Use%20Policy.pdf?sp=r&st=2025-06-06T00:00:00Z&se=2030-01-01T00:00:00Z&spr=https&sv=2024-11-04&sr=b&sig=1tzDMHnC75Ks0avMCfHEfVwVAtFB8udbFcGSBvM0My8%3D")}
                    >
                        Acceptable Use Policy
                    </Link>
                </Typography>
                <Typography variant="caption">
                    <Link
                        component="button"
                        color="info"
                        onClick={() => openExternalUrl(aboutlicensesUrl)}
                    >
                        Third Party Licenses
                    </Link>
                </Typography>
            </Stack>
        </Stack>
    </Drawer>
}

function LoggedInAs() {
    const { isPending, data } = new ApiQueries.contact.currentUser().ToQuery();
    
    if (isPending)
        return <Stack>
            <Skeleton variant="text" width={100} />
            <Skeleton variant="text" width={100} sx={{ fontSize: '2rem'}} />
        </Stack>

    return <Stack spacing={1} alignItems="flex-start">
        <Typography variant="caption">Logged in as</Typography>
        <Typography variant="h3">{data!.DisplaynameSurname}</Typography>
    </Stack>
    
}

function Menus({menus, onClose}:{menus:MainMenuItem[], onClose: () => void}) {
    const { User, Post, SignOut, ClientId, IsImpersonating, Unimpersonate } = useAuth();
    const { baseUrl, ClearSchool, schoolCode } = useSchool();
    const [resetLoading, setResetLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [isError, setIsError] = useState(false);
    const toastRef = useRef<ToastControls | null>(null);
    const navigate = useNavigate();
    const { mode, setMode } = useColorScheme();
    const [unimpersonating, setUnimpersonating] = useState(false);

    const resetPassword = async () => {
        setResetLoading(true);
        try {
            const response = await Post<{response:string}>({
                url:baseUrl + `/lms/api/password/reset?redirectUrl=daymap-companion://${schoolCode}/auth&clientId=${ClientId}`,
                data:null,
                config:null
            });
            setMessage(response.response);
            toastRef.current?.openToast();
            setIsError(false);
        } catch(error:any){
            console.log(error);
            setMessage(error.message != undefined ? error.message : "There was a problem resetting your password. Please try again later or contact your school.");
            toastRef.current?.openToast();
            setIsError(true);
        }
        setResetLoading(false);
    }

    
    const signOut = () => {
        if (IsImpersonating) {
            setUnimpersonating(true);
            return Unimpersonate();
        }

        SignOut();
        ClearSchool()
    }

    return <>
        <Toast type={isError ? "error" : "success"} message={message} ref={toastRef} />
        <List>
            <ListItem sx={{ p: 0 }}>
                <ListItemIcon>
                    <DaymapIcon iconType={mode === "dark" ? DaymapIconType.DarkMode : DaymapIconType.LightMode} />
                </ListItemIcon>
                <ListItemText sx={{ pr: 2}}>
                    Theme
                </ListItemText>
                <Select value={mode}
                    onChange={e => setMode(e.target.value as "system" | "light" | "dark")}
                >
                    <MenuItem value="system">System</MenuItem>
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                </Select>
            </ListItem>
            {User?.STS == 4 && !IsImpersonating && !unimpersonating &&
                <ListItemButton sx={{px:0}} disabled={resetLoading} onClick={resetPassword}>
                    <ListItemIcon>
                        <DaymapIcon iconType={DaymapIconType.PasswordReset} />
                    </ListItemIcon>
                    <ListItemText>Reset Password</ListItemText>
                    {resetLoading && <CircularProgress size={24} />}
                </ListItemButton>
            }
            {menus.map(m => <ListItemButton sx={{px:0}} onClick={() => {
                navigate(m.to);
                onClose();
            }}>
                <ListItemIcon><DaymapIcon iconType={m.iconType} /></ListItemIcon>
                <ListItemText>{m.label}</ListItemText>
            </ListItemButton>)}
            <ListItemButton sx={{px:0}} onClick={signOut}>
                <ListItemIcon><DaymapIcon iconType={DaymapIconType.SignOut} /></ListItemIcon>
                <ListItemText>Sign Out</ListItemText>
            </ListItemButton>
        </List>
    </>
}